// src/pages/Home/LastNews.jsx

import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import createDOMPurify from "dompurify";
import {
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiMaximize2,
  FiX,
} from "react-icons/fi";
import {
  FaExclamationTriangle,
  FaNewspaper,
  FaRegClock,
  FaUserEdit,
} from "react-icons/fa";

import { getLatestNews } from "../../services/newsServices";
import { toAbsolutePhotoUrl } from "../../services/newsPhotoServices";

const FALLBACK = "/img/no-image.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et gestion sécurisée des styles enrichis
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (
      node.tagName === "A" &&
      node.getAttribute("target") === "_blank"
    ) {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

/* -------------------------------------------------------
   Classes communes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "rich-news-content",
  "prose prose-slate max-w-none break-words",
  "text-[15px] leading-7 text-slate-600 sm:text-base",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes et retours à la ligne
  "prose-p:my-4",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-primary",
  "hover:prose-a:underline",

  // Listes à puces
  "[&_ul]:!my-5",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-7",

  // Listes numérotées
  "[&_ol]:!my-5",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-7",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-6",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-6",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Citations
  "prose-blockquote:border-primary",
  "prose-blockquote:bg-orange-50",
  "prose-blockquote:px-5",
  "prose-blockquote:py-3",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-orange-500/10",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

function toAbs(url) {
  const cleanUrl = String(url || "").trim();

  const absoluteUrl = cleanUrl
    ? toAbsolutePhotoUrl(cleanUrl)
    : "";

  return absoluteUrl || FALLBACK;
}

function stripHtml(html = "") {
  if (typeof window === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "");
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent || div.innerText || "";
}

function sanitizeHtml(html = "") {
  if (!purify) return "";

  return purify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
}

function authorLabel(news) {
  const prenom = news?.author?.prenom || "";
  const nom = news?.author?.nom || "";
  const fullName = `${prenom} ${nom}`.trim();

  return fullName || "Club";
}

function getNewsDate(news) {
  const rawDate =
    news?.published_at ||
    news?.date_publication ||
    news?.date_news ||
    news?.createdAt ||
    news?.updatedAt;

  if (!rawDate) return "";

  const date = new Date(rawDate);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/* -------------------------------------------------------
   États de chargement
-------------------------------------------------------- */

function NewsSkeleton() {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-44 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />

          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-14 overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
            <div className="h-80 animate-pulse bg-slate-200 dark:bg-slate-800 lg:h-[460px]" />

            <div className="space-y-4 p-6 sm:p-8 lg:p-10">
              <div className="h-8 w-40 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

              <div className="h-10 w-3/4 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />

              <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

              <div className="h-4 w-11/12 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

              <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaNewspaper />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucune actualité disponible
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Les dernières nouvelles du club apparaîtront ici dès
        qu’elles seront publiées.
      </p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   Composant principal
-------------------------------------------------------- */

export default function LatestNews() {
  const [newsList, setNewsList] = useState([]);
  const [index, setIndex] = useState(0);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [animDir, setAnimDir] = useState("next");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [isPreviewTruncated, setIsPreviewTruncated] =
    useState(false);

  const previewContentRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const fetchNews = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const latest = await getLatestNews();

        const activeNews = (
          Array.isArray(latest) ? latest : []
        )
          .filter((news) => {
            if (
              news?.is_active === undefined ||
              news?.is_active === null ||
              news?.is_active === ""
            ) {
              return true;
            }

            return (
              news.is_active === true ||
              news.is_active === 1 ||
              news.is_active === "1"
            );
          })
          .slice(0, 6);

        if (!mounted) return;

        setNewsList(activeNews);
        setIndex(0);
      } catch (error) {
        console.error(
          "Erreur lors du chargement des actualités :",
          error,
        );

        if (mounted) {
          setNewsList([]);
          setErrorMsg(
            "Impossible de charger les dernières actualités.",
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchNews();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const isModalOpen = !!selectedNews || !!selectedImage;

    if (!isModalOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      setSelectedNews(null);
      setSelectedImage(null);
    };

    const previousOverflow = document.body.style.overflow;

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedNews, selectedImage]);

  const total = newsList.length;

  const current = useMemo(() => {
    return newsList[index] || null;
  }, [newsList, index]);

  const currentImage = current
    ? toAbs(current.image_url)
    : FALLBACK;

  const currentDate = current ? getNewsDate(current) : "";

  const safeCurrentNewsHtml = useMemo(() => {
    const rawContent = current?.content || "";
    const plainContent = stripHtml(rawContent).trim();

    if (!plainContent) {
      return sanitizeHtml(
        "<p>Le contenu de cette actualité sera bientôt disponible.</p>",
      );
    }

    return sanitizeHtml(rawContent);
  }, [current?.content]);

  const safeSelectedNewsHtml = useMemo(() => {
    const rawContent = selectedNews?.content || "";
    const plainContent = stripHtml(rawContent).trim();

    if (!plainContent) {
      return sanitizeHtml(
        "<p>Le contenu de cette actualité sera bientôt disponible.</p>",
      );
    }

    return sanitizeHtml(rawContent);
  }, [selectedNews?.content]);

  /*
   * Vérifie si l’aperçu dépasse la hauteur maximale autorisée.
   * Le dégradé n’est affiché que lorsque le contenu est réellement coupé.
   */
  useLayoutEffect(() => {
    const previewElement = previewContentRef.current;

    if (!previewElement) {
      setIsPreviewTruncated(false);
      return undefined;
    }

    let animationFrameId;

    const checkOverflow = () => {
      animationFrameId = window.requestAnimationFrame(() => {
        const hasOverflow =
          previewElement.scrollHeight >
          previewElement.clientHeight + 2;

        setIsPreviewTruncated(hasOverflow);
      });
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkOverflow)
        : null;

    resizeObserver?.observe(previewElement);

    window.addEventListener("resize", checkOverflow);

    const previewImages =
      previewElement.querySelectorAll("img");

    previewImages.forEach((image) => {
      image.addEventListener("load", checkOverflow);
      image.addEventListener("error", checkOverflow);
    });

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkOverflow);

      previewImages.forEach((image) => {
        image.removeEventListener("load", checkOverflow);
        image.removeEventListener("error", checkOverflow);
      });
    };
  }, [safeCurrentNewsHtml, current?.id]);

  const go = (nextIndex, direction) => {
    if (!total) return;

    setAnimDir(direction);
    setIndex((nextIndex + total) % total);
  };

  const prev = () => go(index - 1, "prev");
  const next = () => go(index + 1, "next");

  const openArticle = () => {
    if (current) {
      setSelectedNews(current);
    }
  };

  const closeArticle = () => {
    setSelectedNews(null);
  };

  const openImageModal = () => {
    if (current) {
      setSelectedImage(toAbs(current.image_url));
    }
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const onImgError = (event) => {
    const image = event?.currentTarget;

    if (!image) return;

    if (image.dataset.fallbackApplied === "true") return;

    image.dataset.fallbackApplied = "true";
    image.src = FALLBACK;
  };

  if (loading) return <NewsSkeleton />;

  return (
    <section
      id="latest-news"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <style>{`
        @keyframes ln-in-next {
          from {
            opacity: 0;
            transform: translateX(22px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes ln-in-prev {
          from {
            opacity: 0;
            transform: translateX(-22px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .rich-news-content ul {
          margin-top: 1.25rem;
          margin-bottom: 1.25rem;
          padding-left: 1.75rem;
          list-style-type: disc;
        }

        .rich-news-content ol {
          margin-top: 1.25rem;
          margin-bottom: 1.25rem;
          padding-left: 1.75rem;
          list-style-type: decimal;
        }

        .rich-news-content li {
          display: list-item;
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
          padding-left: 0.25rem;
          white-space: pre-wrap;
        }

        .rich-news-content ul ul {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
          padding-left: 1.5rem;
          list-style-type: circle;
        }

        .rich-news-content ul ul ul {
          list-style-type: square;
        }

        .rich-news-content ol ol {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
          padding-left: 1.5rem;
          list-style-type: lower-alpha;
        }

        .rich-news-content ol ol ol {
          list-style-type: lower-roman;
        }

        .rich-news-content li[data-list="bullet"] {
          list-style-type: disc;
        }

        .rich-news-content li[data-list="ordered"] {
          list-style-type: decimal;
        }

        .rich-news-content .ql-align-center {
          text-align: center;
        }

        .rich-news-content .ql-align-right {
          text-align: right;
        }

        .rich-news-content .ql-align-justify {
          text-align: justify;
        }

        .rich-news-content .ql-indent-1 {
          margin-left: 1.5rem;
        }

        .rich-news-content .ql-indent-2 {
          margin-left: 3rem;
        }

        .rich-news-content .ql-indent-3 {
          margin-left: 4rem;
        }

        .rich-news-content .ql-indent-4 {
          margin-left: 5rem;
        }

        .rich-news-content .ql-indent-5 {
          margin-left: 6rem;
        }

        .rich-news-content .ql-size-small {
          font-size: 0.875em;
        }

        .rich-news-content .ql-size-large {
          font-size: 1.5em;
        }

        .rich-news-content .ql-size-huge {
          font-size: 2.25em;
          line-height: 1.2;
        }

        .rich-news-content .ql-font-serif {
          font-family: Georgia, Cambria, "Times New Roman", serif;
        }

        .rich-news-content .ql-font-monospace {
          font-family: Monaco, Consolas, "Liberation Mono", monospace;
        }

        @media (prefers-reduced-motion: reduce) {
          .ln-anim {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaNewspaper />
            Actualités du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Dernières{" "}
            <span className="text-primary">actualités</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            {total
              ? "Retrouvez les dernières informations, annonces et temps forts publiés par le club."
              : "Aucune actualité pour le moment."}
          </p>
        </div>

        <div className="mt-14">
          {errorMsg ? <ErrorState message={errorMsg} /> : null}

          {!errorMsg && !current ? <EmptyState /> : null}

          {!errorMsg && current ? (
            <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900">
              {total > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    className="absolute left-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-800 shadow-xl backdrop-blur-xl transition hover:scale-105 hover:text-primary dark:border-white/10 dark:bg-slate-950/80 dark:text-white md:flex"
                    aria-label="Actualité précédente"
                  >
                    <FiChevronLeft className="text-xl" />
                  </button>

                  <button
                    type="button"
                    onClick={next}
                    className="absolute right-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-800 shadow-xl backdrop-blur-xl transition hover:scale-105 hover:text-primary dark:border-white/10 dark:bg-slate-950/80 dark:text-white md:flex"
                    aria-label="Actualité suivante"
                  >
                    <FiChevronRight className="text-xl" />
                  </button>
                </>
              ) : null}

              <div
                key={current.id || index}
                className={cn(
                  "ln-anim grid lg:grid-cols-[0.95fr_1.05fr]",
                  animDir === "next"
                    ? "[animation:ln-in-next_.32s_ease-out]"
                    : "[animation:ln-in-prev_.32s_ease-out]",
                )}
              >
                <div className="relative min-h-[320px] overflow-hidden lg:min-h-[500px]">
                  <button
                    type="button"
                    onClick={openImageModal}
                    className="block h-full w-full"
                    aria-label="Agrandir l’image"
                    title="Agrandir l’image"
                  >
                    <img
                      src={currentImage}
                      alt={current.title || "Illustration"}
                      loading="lazy"
                      decoding="async"
                      onError={onImgError}
                      className="h-full w-full object-cover transition duration-700 hover:scale-105"
                    />
                  </button>

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />

                  <button
                    type="button"
                    onClick={openImageModal}
                    className="absolute right-5 top-5 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white/35 bg-white/95 text-slate-950 shadow-2xl backdrop-blur-xl transition hover:scale-105 hover:bg-primary hover:text-white"
                    aria-label="Agrandir l’image"
                    title="Agrandir l’image"
                  >
                    <FiMaximize2 className="text-lg" />
                  </button>

                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur-xl">
                        <FaUserEdit className="text-primary" />
                        {authorLabel(current)}
                      </span>

                      {currentDate ? (
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/15 px-4 py-2 text-xs font-bold text-white/80 backdrop-blur-xl">
                          <FaRegClock className="text-primary" />
                          {currentDate}
                        </span>
                      ) : null}
                    </div>

                    <h3 className="mt-5 max-w-2xl font-barlow text-3xl font-extrabold leading-tight text-white drop-shadow-lg sm:text-4xl">
                      {current.title}
                    </h3>
                  </div>
                </div>

                <div className="relative flex flex-col p-6 sm:p-8 lg:p-10">
                  <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-primary/10 blur-3xl" />

                  <div className="relative flex items-center justify-between gap-4">
                    <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                      <FiFileText />
                      Article du club
                    </div>

                    {total > 1 ? (
                      <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-500 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                        {index + 1} / {total}
                      </div>
                    ) : null}
                  </div>

                  <h3 className="relative mt-6 font-barlow text-3xl font-extrabold leading-tight text-slate-950 dark:text-white sm:text-4xl">
                    {current.title}
                  </h3>

                  {/* Aperçu enrichi limité en hauteur */}

                  <div className="relative mt-5">
                    <div
                      ref={previewContentRef}
                      className={cn(
                        RICH_TEXT_CLASSES,
                        "max-h-[230px] overflow-hidden pr-1 sm:max-h-[250px] lg:max-h-[270px]",
                      )}
                      style={{ tabSize: 4 }}
                      dangerouslySetInnerHTML={{
                        __html: safeCurrentNewsHtml,
                      }}
                    />

                    {isPreviewTruncated ? (
                      <div
                        className={cn(
                          "pointer-events-none absolute inset-x-0 bottom-0 h-24",
                          "bg-gradient-to-t from-white via-white/95 to-transparent",
                          "dark:from-slate-900 dark:via-slate-900/95",
                        )}
                        aria-hidden="true"
                      />
                    ) : null}
                  </div>

                  <div className="relative mt-8 flex flex-wrap items-center justify-between gap-3">
                    {total > 1 ? (
                      <div className="flex gap-2 md:hidden">
                        <button
                          type="button"
                          onClick={prev}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-white"
                          aria-label="Actualité précédente"
                        >
                          <FiChevronLeft />
                        </button>

                        <button
                          type="button"
                          onClick={next}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-white"
                          aria-label="Actualité suivante"
                        >
                          <FiChevronRight />
                        </button>
                      </div>
                    ) : null}

                    <button
                      type="button"
                      onClick={openArticle}
                      className="ml-auto inline-flex items-center justify-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-white"
                      aria-label="Lire l'article complet"
                    >
                      <FiFileText />
                      Lire la suite
                    </button>
                  </div>

                  {total > 1 ? (
                    <div className="relative mt-8 flex items-center justify-center gap-2">
                      {newsList.map((news, dotIndex) => (
                        <button
                          key={news.id || dotIndex}
                          type="button"
                          onClick={() =>
                            go(
                              dotIndex,
                              dotIndex > index ? "next" : "prev",
                            )
                          }
                          className={cn(
                            "h-2.5 rounded-full transition-all",
                            dotIndex === index
                              ? "w-8 bg-primary"
                              : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600",
                          )}
                          aria-label={`Aller à l'actualité ${
                            dotIndex + 1
                          }`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90" />
            </div>
          ) : null}
        </div>
      </div>

      {/* Modale de lecture de l’article */}

      {selectedNews ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 px-3 py-6 backdrop-blur-md"
          onClick={closeArticle}
          role="dialog"
          aria-modal="true"
          aria-label={`Article : ${
            selectedNews.title || "Actualité"
          }`}
        >
          <div
            className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeArticle}
              className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/85 text-slate-700 shadow-lg backdrop-blur transition hover:text-primary focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/30 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200"
              aria-label="Fermer l’article"
            >
              <FiX className="text-xl" />
            </button>

            <div className="grid max-h-[92vh] overflow-y-auto overflow-x-hidden lg:grid-cols-[0.85fr_1.15fr]">
              <div className="relative min-h-[300px] overflow-hidden lg:min-h-full">
                <img
                  src={toAbs(selectedNews.image_url)}
                  alt={selectedNews.title || "Illustration"}
                  className="h-full w-full object-cover"
                  onError={onImgError}
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />

                <div className="absolute bottom-6 left-6 right-6">
                  <span className="inline-flex rounded-full border border-white/15 bg-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-white/75 backdrop-blur-xl">
                    {authorLabel(selectedNews)}
                  </span>

                  <h2 className="mt-4 font-barlow text-3xl font-extrabold leading-tight text-white drop-shadow-xl">
                    {selectedNews.title}
                  </h2>
                </div>
              </div>

              <div className="p-6 text-slate-900 dark:text-white sm:p-8 lg:p-10">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                  <FiFileText />
                  Article complet
                </div>

                <h2 className="mt-6 font-barlow text-3xl font-extrabold text-slate-950 dark:text-white">
                  {selectedNews.title}
                </h2>

                <div className="mt-5 max-h-[55vh] overflow-y-auto pr-2">
                  <div
                    className={RICH_TEXT_CLASSES}
                    style={{ tabSize: 4 }}
                    dangerouslySetInnerHTML={{
                      __html: safeSelectedNewsHtml,
                    }}
                  />
                </div>

                <div className="mt-8">
                  <button
                    type="button"
                    onClick={closeArticle}
                    className="inline-flex items-center justify-center rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modale d’agrandissement de l’image */}

      {selectedImage ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/90 px-3 py-6 backdrop-blur-md"
          onClick={closeImageModal}
          role="dialog"
          aria-modal="true"
          aria-label="Image de l’actualité agrandie"
        >
          <div
            className="relative w-full max-w-6xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeImageModal}
              className="absolute -right-3 -top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/90 text-slate-700 shadow-xl transition hover:text-primary focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 dark:bg-slate-900 dark:text-slate-200"
              aria-label="Fermer l’image"
            >
              <FiX className="text-xl" />
            </button>

            <img
              src={selectedImage}
              alt="Agrandissement de l’actualité"
              className="max-h-[82vh] w-full rounded-[2rem] border border-white/20 bg-black/20 object-contain shadow-2xl"
              onError={onImgError}
            />

            <p className="mt-4 text-center text-xs text-white/70">
              Cliquez en dehors de l’image ou appuyez sur Échap pour
              fermer.
            </p>
          </div>
        </div>
      ) : null}
    </section>
  );
}